import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronIcon } from './icons.jsx';

const SECTIONS = [
  {
    title: 'Tableau de bord',
    icon: '🏠',
    path: '/',
    role: 'Tous',
    items: [
      { q: 'À quoi sert le tableau de bord ?', a: 'C\'est la page d\'accueil. Elle affiche un résumé de la campagne en cours : nombre de clients, livrés, en attente, problèmes. Pour les livreurs, elle montre directement la tournée du jour avec un bouton "Démarrer".' },
      { q: 'Comment voir la progression ?', a: 'Les statistiques en haut de page montrent le taux de livraison en temps réel. La barre de progression se met à jour automatiquement.' },
    ],
  },
  {
    title: 'Gestion des clients',
    icon: '👥',
    path: '/clients',
    role: 'Coordinateur, Admin',
    items: [
      { q: 'Comment rechercher un client ?', a: 'Tapez dans la barre de recherche. La recherche est floue : même avec une faute de frappe, le client sera trouvé (ex: "Dupon" trouvera "Dupont").' },
      { q: 'Comment filtrer les clients ?', a: 'Utilisez les filtres sous la barre de recherche : par statut (en attente, livré, problème), par livreur, ou par ville.' },
      { q: 'Comment voir le détail d\'un client ?', a: 'Cliquez sur un client dans la liste. Vous verrez ses informations complètes, sa position sur la carte, l\'historique des livraisons et les articles (vélos) associés.' },
      { q: 'Comment importer des clients ?', a: 'Allez dans Import (menu). Préparez un fichier Excel avec les colonnes : name, address, city, phone, nb_units, lat, lng. Déposez le fichier et validez.' },
    ],
  },
  {
    title: 'Import de données',
    icon: '📥',
    path: '/import',
    role: 'Coordinateur, Admin',
    items: [
      { q: 'Quels fichiers peut-on importer ?', a: 'Trois types : 1) Fichier clients (Excel avec colonnes name, address, city, etc.), 2) Fichier articles/vélos (avec FNUCI, client associé), 3) Fichier DS3 de correspondance boîte→FNUCI pour la validation croisée.' },
      { q: 'Qu\'est-ce que l\'import DS3 ?', a: 'Le DS3 est un tableau de correspondance entre numéros de boîte et numéros FNUCI. Il permet de valider automatiquement les scans OCR lors de la livraison : si le FNUCI scanné correspond à la boîte attendue, c\'est validé.' },
    ],
  },
  {
    title: 'Tournées (Plans de livraison)',
    icon: '🚚',
    path: '/plans',
    role: 'Coordinateur, Admin',
    items: [
      { q: 'Comment créer une tournée manuellement ?', a: 'Allez dans Tournées → "Nouvelle tournée". Choisissez la date, l\'heure de départ, le livreur, et cochez les clients à livrer. La jauge de capacité vous indique le remplissage du véhicule (42 vélos par défaut).' },
      { q: 'Comment utiliser la suggestion d\'itinéraire ?', a: 'Allez dans Tournées → "Suggestion". Le système propose 2-3 itinéraires optimisés automatiquement (plus court, éloignés d\'abord, etc.) avec aperçu sur carte et distance estimée.' },
      { q: 'Comment fonctionne le mode multi-jours ?', a: 'Quand le nombre total d\'unités dépasse la capacité du véhicule, un toggle "Multi-jours" apparaît. Activez-le : le système répartit automatiquement les clients sur plusieurs jours ouvrables (en sautant les week-ends). Un clic crée toutes les tournées.' },
      { q: 'Comment réordonner les arrêts ?', a: 'Dans le détail d\'une tournée, utilisez les flèches haut/bas sur chaque arrêt, ou le glisser-déposer sur desktop. Les heures d\'arrivée estimées sont recalculées automatiquement.' },
      { q: 'Comment déplacer un client vers une autre tournée ?', a: 'Sur un arrêt, cliquez "Déplacer". Choisissez la tournée de destination. L\'arrêt est transféré avec renumérotation automatique.' },
      { q: 'Comment dupliquer une tournée ?', a: 'Dans le détail d\'une tournée, cliquez "Dupliquer". Choisissez une nouvelle date. Tous les arrêts sont copiés.' },
      { q: 'Comment fonctionne la pause déjeuner ?', a: 'Lors de la création, configurez l\'heure de pause et sa durée. Les ETA de tous les arrêts après la pause sont automatiquement décalés.' },
      { q: 'Les fenêtres horaires sont-elles prises en compte ?', a: 'Oui. Si un client a un créneau (ex: "matin", "14h-16h"), l\'optimisation place les clients "matin" en premier, "après-midi" en dernier. Les heures précises sont triées dans leur groupe.' },
    ],
  },
  {
    title: 'Mode terrain (Livraison)',
    icon: '🚴',
    path: null,
    role: 'Livreur, Coordinateur',
    items: [
      { q: 'Comment démarrer une livraison ?', a: 'Depuis le tableau de bord (livreur) → "Démarrer la tournée", ou depuis le détail d\'une tournée → "Mode terrain". Le mode terrain est plein écran avec les étapes une par une.' },
      { q: 'Quelles sont les étapes de livraison ?', a: '5 étapes par client : 1) Arrivée (signaler qu\'on est sur place), 2) Scan FNUCI (OCR automatique ou saisie manuelle), 3) Photo de livraison, 4) Signature du client, 5) Prise de RDV contrôle.' },
      { q: 'Comment fonctionne le scan FNUCI ?', a: 'Pointez la caméra sur l\'étiquette FNUCI du vélo. L\'OCR lit automatiquement le numéro (format AV + 8 caractères). Si le DS3 est importé, le numéro est validé par correspondance croisée avec le numéro de boîte.' },
      { q: 'Que faire si le scan ne fonctionne pas ?', a: 'Cliquez "Saisie manuelle" et tapez le numéro FNUCI à la main. Vous pouvez aussi prendre une photo de l\'étiquette.' },
      { q: 'Comment naviguer vers le client ?', a: 'Un bouton "Naviguer vers le client" ouvre Apple Maps (iPhone) ou Google Maps avec l\'itinéraire automatique. Disponible à chaque étape.' },
      { q: 'Comment signaler un problème ?', a: 'Sur un arrêt, cliquez "Problème". Choisissez le type (absent, refus, adresse incorrecte, etc.) et ajoutez un commentaire. Le coordinateur est notifié.' },
      { q: 'Comment reporter un arrêt ?', a: 'Cliquez "Reporter" sur l\'arrêt. Il passe en statut "Ignoré" et peut être reprogrammé dans une autre tournée.' },
      { q: 'Comment passer d\'un arrêt à un autre ?', a: 'Cliquez sur "Ma tournée" en haut pour voir la liste des arrêts. Cliquez sur n\'importe quel arrêt pour y accéder directement.' },
      { q: 'Qu\'est-ce que le géofencing ?', a: 'Quand vous approchez à moins de 150m d\'un client, une alerte apparaît pour vous prévenir. Aucune action n\'est déclenchée automatiquement, c\'est juste une aide visuelle.' },
      { q: 'L\'application fonctionne-t-elle hors ligne ?', a: 'Partiellement. Les données déjà chargées restent accessibles. Les actions (scan, signature, statut) sont mises en file d\'attente et synchronisées au retour de la connexion. Un indicateur en haut montre l\'état de la connexion.' },
    ],
  },
  {
    title: 'Carte interactive',
    icon: '🗺️',
    path: '/map',
    role: 'Tous',
    items: [
      { q: 'Que montre la carte ?', a: 'Tous les clients de la campagne avec des marqueurs colorés selon leur statut (en attente, livré, problème). Le dépôt est affiché en bleu. Les lignes de la tournée active sont tracées.' },
      { q: 'Comment livrer directement depuis la carte ?', a: 'Cliquez sur un marqueur client, puis sur "Livrer" dans le popup. Cela ouvre le mode terrain directement sur ce client.' },
      { q: 'Les itinéraires sont-ils réels ?', a: 'Oui, les tracés utilisent OSRM (Open Source Routing Machine) qui calcule les itinéraires sur les vraies routes, pas à vol d\'oiseau.' },
    ],
  },
  {
    title: 'Statistiques',
    icon: '📊',
    path: '/stats',
    role: 'Coordinateur, Admin',
    items: [
      { q: 'Quelles stats sont disponibles ?', a: 'Taux de livraison global, performance par livreur (nombre de livraisons, temps moyen), statistiques de scan (OCR vs manuel), répartition des problèmes par type, et comparaison entre campagnes.' },
      { q: 'Comment exporter les données ?', a: 'Sur la page Stats, des boutons d\'export permettent de télécharger les données en Excel (clients, livraisons, scans).' },
    ],
  },
  {
    title: 'Chat d\'équipe',
    icon: '💬',
    path: '/chat',
    role: 'Tous',
    items: [
      { q: 'Comment fonctionne le chat ?', a: 'C\'est une messagerie interne entre coordinateurs et livreurs, organisée par tournée. Sélectionnez une tournée, puis envoyez vos messages. Les messages sont rafraîchis automatiquement toutes les 10 secondes.' },
      { q: 'Peut-on envoyer des photos ?', a: 'Oui, utilisez l\'icône trombone pour joindre une photo à un message.' },
    ],
  },
  {
    title: 'SMS automatiques',
    icon: '📱',
    path: '/sms',
    role: 'Coordinateur, Admin',
    items: [
      { q: 'Quand les SMS sont-ils envoyés ?', a: 'Un SMS est envoyé automatiquement au prochain client quand le livreur signale son arrivée à l\'arrêt en cours. Le SMS contient l\'heure d\'arrivée estimée et un lien de suivi.' },
      { q: 'Qu\'est-ce que le lien de suivi ?', a: 'Chaque SMS contient un lien unique permettant au client de suivre la livraison en temps réel : position du livreur, ETA, progression de la tournée. Aucune connexion requise.' },
      { q: 'Comment voir l\'historique des SMS ?', a: 'La page SMS affiche tous les SMS envoyés avec leur statut (envoyé, erreur) et leur contenu.' },
    ],
  },
  {
    title: 'Suivi public (Tracking)',
    icon: '📍',
    path: null,
    role: 'Client final',
    items: [
      { q: 'Comment le client suit-il sa livraison ?', a: 'Le client reçoit un lien par SMS (ex: velo.../track/abc123). En l\'ouvrant, il voit en temps réel : le nom du livreur, l\'heure d\'arrivée estimée, le statut de sa livraison, et la progression globale de la tournée.' },
      { q: 'Comment générer les liens de suivi ?', a: 'Dans le détail d\'une tournée, cliquez "Générer liens de suivi". Les liens sont automatiquement inclus dans les SMS.' },
    ],
  },
  {
    title: 'Rendez-vous de contrôle',
    icon: '📅',
    path: '/appointments',
    role: 'Coordinateur, Admin',
    items: [
      { q: 'À quoi servent les RDV ?', a: 'Après la livraison, un RDV de contrôle est planifié pour vérifier que les vélos sont bien livrés et que les numéros FNUCI correspondent au tableau. Le livreur propose un créneau lors de la livraison.' },
      { q: 'Comment voir les RDV ?', a: 'La page RDV affiche la liste et un calendrier. Filtrez par statut : planifié, terminé, absent, annulé.' },
    ],
  },
  {
    title: 'Historique des scans',
    icon: '📷',
    path: '/scan-history',
    role: 'Coordinateur, Admin',
    items: [
      { q: 'Que montre l\'historique ?', a: 'Tous les scans FNUCI du jour : numéro de série, heure, méthode (OCR auto, OCR avec rotation, ou saisie manuelle), et taux de confiance.' },
    ],
  },
  {
    title: 'Administration',
    icon: '⚙️',
    path: '/admin/users',
    role: 'Admin',
    items: [
      { q: 'Comment ajouter un utilisateur ?', a: 'Allez dans Admin → Utilisateurs. Cliquez "Inviter" pour générer un lien d\'invitation. Partagez le lien : l\'utilisateur s\'inscrit et rejoint la campagne avec le rôle choisi.' },
      { q: 'Comment changer le rôle d\'un utilisateur ?', a: 'Dans la liste des utilisateurs, utilisez le menu déroulant pour changer le rôle (admin, coordinateur, livreur).' },
      { q: 'Quels sont les rôles ?', a: 'Admin : accès total, gestion des utilisateurs. Coordinateur : création de tournées, suivi des livraisons, stats. Livreur : mode terrain uniquement, ses tournées.' },
      { q: 'Comment configurer les champs visibles ?', a: 'Admin → Visibilité. Choisissez quels champs client sont affichés dans les listes et fiches (utile pour masquer des données sensibles).' },
    ],
  },
  {
    title: 'Mode Bureau / Terrain',
    icon: '🔄',
    path: null,
    role: 'Coordinateur',
    items: [
      { q: 'Quelle est la différence ?', a: 'Le mode Bureau affiche toutes les fonctionnalités (clients, stats, admin...). Le mode Terrain simplifie l\'interface pour la livraison : uniquement les tournées, le chat et la carte.' },
      { q: 'Comment basculer ?', a: 'Cliquez le bouton "Terrain" / "Bureau" dans l\'en-tête. Le coordinateur peut ainsi alterner entre la gestion au bureau et la livraison sur le terrain.' },
    ],
  },
  {
    title: 'Connexion et sécurité',
    icon: '🔐',
    path: '/login',
    role: 'Tous',
    items: [
      { q: 'Comment se connecter ?', a: 'Deux méthodes : mot de passe classique ou PIN rapide (6 chiffres). Le PIN est plus pratique pour les livreurs sur le terrain. L\'email est mémorisé pour les connexions suivantes.' },
      { q: 'Comment configurer son PIN ?', a: 'Après la première connexion par mot de passe, un PIN peut être défini depuis les paramètres du profil.' },
      { q: 'Comment gérer plusieurs campagnes ?', a: 'Si vous avez accès à plusieurs campagnes, un sélecteur apparaît dans l\'en-tête. Changez de campagne à tout moment.' },
    ],
  },
];

export default function HelpPage() {
  const [openSection, setOpenSection] = useState(null);
  const [openItem, setOpenItem] = useState(null);
  const [search, setSearch] = useState('');

  const filtered = search.trim()
    ? SECTIONS.map((s) => ({
        ...s,
        items: s.items.filter(
          (i) =>
            i.q.toLowerCase().includes(search.toLowerCase()) ||
            i.a.toLowerCase().includes(search.toLowerCase())
        ),
      })).filter((s) => s.items.length > 0 || s.title.toLowerCase().includes(search.toLowerCase()))
    : SECTIONS;

  return (
    <div className="animate-fade-in max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-[#0f172a] mb-1">Aide</h2>
      <p className="text-sm text-[#64748b] mb-5">
        Toutes les fonctionnalités de Velogistique et comment les utiliser.
      </p>

      {/* Search */}
      <div className="relative mb-5">
        <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
        </svg>
        <input
          type="text"
          placeholder="Rechercher une fonctionnalité..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-[#1e40af] focus:border-[#1e40af] focus:outline-none"
        />
      </div>

      {/* Sections */}
      <div className="space-y-3">
        {filtered.map((section, si) => {
          const isOpen = openSection === si || search.trim().length > 0;
          return (
            <div key={si} className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <button
                onClick={() => setOpenSection(isOpen && !search ? null : si)}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl">{section.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-[#0f172a]">{section.title}</div>
                  <div className="text-xs text-[#64748b]">{section.role}</div>
                </div>
                {section.path && (
                  <Link
                    to={section.path}
                    onClick={(e) => e.stopPropagation()}
                    className="text-[10px] bg-blue-50 text-[#1e40af] px-2 py-1 rounded-lg font-medium hover:bg-blue-100 shrink-0"
                  >
                    Ouvrir
                  </Link>
                )}
                <ChevronIcon
                  size={16}
                  className={`text-gray-400 transition-transform shrink-0 ${isOpen ? 'rotate-90' : ''}`}
                />
              </button>

              {isOpen && (
                <div className="px-4 pb-4 space-y-2">
                  {section.items.map((item, ii) => {
                    const itemKey = `${si}-${ii}`;
                    const itemOpen = openItem === itemKey || search.trim().length > 0;
                    return (
                      <div key={ii} className="border border-gray-100 rounded-xl overflow-hidden">
                        <button
                          onClick={() => setOpenItem(itemOpen && !search ? null : itemKey)}
                          className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="text-xs font-medium text-[#1e40af] bg-blue-50 w-5 h-5 rounded-full flex items-center justify-center shrink-0">?</span>
                          <span className="text-sm text-[#0f172a] flex-1">{item.q}</span>
                          <ChevronIcon
                            size={14}
                            className={`text-gray-300 transition-transform shrink-0 ${itemOpen ? 'rotate-90' : ''}`}
                          />
                        </button>
                        {itemOpen && (
                          <div className="px-3 pb-3 pt-0">
                            <p className="text-sm text-[#64748b] leading-relaxed pl-7">{item.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <p className="text-[#64748b] text-center py-10 text-sm">Aucun résultat pour "{search}"</p>
      )}

      <p className="text-center text-xs text-gray-400 mt-8 mb-4">
        Velogistique v1 — Logistique terrain pour livraison de vélos
      </p>
    </div>
  );
}
